import { validatePromotionalAdImageFile } from '../lib/promotional-ad-media-validation';
import {
  buildPromotionalAdDestinationColumns,
  parseAdminPromotionalAdDestination,
  summarizePromotionalAdDestination,
} from '../lib/promotional-ad-destinations';
import type { SupabaseClient } from '../supabase/client';
import {
  STORE_PROMOTIONAL_AD_BUCKET,
  type CreateStorePromotionalAdInput,
  type DeleteStorePromotionalAdInput,
  type PromotionalAdDestinationCategoryOption,
  type PromotionalAdDestinationProductOption,
  type PromotionalAdErrorKey,
  type ReorderStorePromotionalAdsInput,
  type StorePromotionalAd,
  type StorePromotionalAdStoreContext,
  type StorePromotionalAdWithPreview,
  type UpdateStorePromotionalAdInput,
  type ValidatedPromotionalAdUploadResult,
} from '../types/promotional-ad';

const PROMOTIONAL_AD_SIGNED_URL_TTL_SECONDS = 60 * 10;

type SupabaseErrorLike = {
  readonly code?: string;
  readonly message?: string;
  readonly name?: string;
  readonly statusCode?: string | number;
  readonly constraint?: string;
  readonly details?: string;
};

type QueryResponse<T> = {
  readonly data: T | null;
  readonly error: SupabaseErrorLike | null;
};

type QueryLike<T> = PromiseLike<QueryResponse<T>> & {
  select(columns?: string): QueryLike<T>;
  update(values: Record<string, unknown>): QueryLike<T>;
  eq(column: string, value: unknown): QueryLike<T>;
  ilike(column: string, value: string): QueryLike<T>;
  in(column: string, values: readonly unknown[]): QueryLike<T>;
  is(column: string, value: unknown): QueryLike<T>;
  limit(count: number): QueryLike<T>;
  maybeSingle(): PromiseLike<QueryResponse<T>>;
  order(column: string, options?: { readonly ascending?: boolean }): QueryLike<T>;
  single(): PromiseLike<QueryResponse<T>>;
};

type RpcInvoker = (
  fn: string,
  args: Record<string, unknown>,
) => PromiseLike<QueryResponse<unknown>>;

function promotionalAdsTable(client: SupabaseClient): QueryLike<unknown> {
  return (client.from as unknown as (relation: string) => QueryLike<unknown>)(
    'store_promotional_ads',
  );
}

function rpc(client: SupabaseClient): RpcInvoker {
  return client.rpc as unknown as RpcInvoker;
}

function trimNullable(value: string | null | undefined): string | null | undefined {
  if (value === undefined) return undefined;
  const trimmed = value?.trim() ?? '';
  return trimmed ? trimmed : null;
}

function appendNullable(formData: FormData, key: string, value: string | null | undefined): void {
  if (value !== undefined) formData.append(key, value ?? '');
}

function appendDestinationFields(
  formData: FormData,
  destination:
    | CreateStorePromotionalAdInput['destination']
    | UpdateStorePromotionalAdInput['destination'],
): void {
  if (!destination) return;
  const columns = buildPromotionalAdDestinationColumns(destination);
  formData.append('destination_type', columns.destination_type);
  appendNullable(formData, 'destination_category_id', columns.destination_category_id);
  appendNullable(formData, 'destination_product_id', columns.destination_product_id);
  appendNullable(formData, 'destination_external_url', columns.destination_external_url);
}

async function getStoreSlug(client: SupabaseClient, storeId: string): Promise<string | null> {
  const { data, error } = await (
    client.from as unknown as (relation: string) => QueryLike<unknown>
  )('stores')
    .select('slug')
    .eq('id', storeId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return ((data as { readonly slug?: string | null } | null)?.slug ?? null) || null;
}

async function hydrateAd(
  client: SupabaseClient,
  row: StorePromotionalAd,
  previewUrl?: string | null,
): Promise<StorePromotionalAdWithPreview> {
  let signedUrl = previewUrl ?? '';
  if (!signedUrl) {
    const { data, error } = await client.storage
      .from(STORE_PROMOTIONAL_AD_BUCKET)
      .createSignedUrl(row.storage_path, PROMOTIONAL_AD_SIGNED_URL_TTL_SECONDS);
    if (error) throw error;
    signedUrl = data.signedUrl;
  }

  const target = await hydrateDestinationTarget(client, row);
  const destination = parseAdminPromotionalAdDestination(row, target);

  return {
    ...row,
    preview_url: signedUrl,
    destination,
    destinationSummary: summarizePromotionalAdDestination(destination),
  };
}

async function invokeStoreAdMedia(
  client: SupabaseClient,
  action: 'create' | 'replace',
  input: CreateStorePromotionalAdInput | UpdateStorePromotionalAdInput,
  image: File,
): Promise<ValidatedPromotionalAdUploadResult> {
  const validation = await validatePromotionalAdImageFile(image);
  if (typeof validation === 'string') {
    throw { code: `PROMOTIONAL_AD_${validation}`, message: validation };
  }

  const formData = new FormData();
  formData.append('action', action);
  formData.append('store_id', input.store_id);
  if ('ad_id' in input) formData.append('ad_id', input.ad_id);
  appendNullable(formData, 'title', trimNullable(input.title));
  appendNullable(formData, 'alt_text', trimNullable(input.alt_text));
  appendDestinationFields(formData, input.destination);
  if (input.is_active !== undefined) formData.append('is_active', String(input.is_active));
  if ('display_order' in input && input.display_order !== undefined) {
    formData.append('display_order', String(input.display_order));
  }
  formData.append('image', image, image.name);

  const headers = await getFunctionAuthHeaders(client);
  const { data, error } = await client.functions.invoke('store-ad-media', {
    body: formData,
    ...(headers ? { headers } : {}),
  });
  if (error) throw await normalizeStoreAdMediaInvokeError(error);
  return data as ValidatedPromotionalAdUploadResult;
}

async function getFunctionAuthHeaders(
  client: SupabaseClient,
): Promise<Record<string, string> | null> {
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : null;
}

async function hydrateDestinationTarget(
  client: SupabaseClient,
  row: StorePromotionalAd,
): Promise<{
  readonly categoryName?: string | null;
  readonly productName?: string | null;
  readonly isPublished?: boolean | null;
  readonly available?: number | null;
  readonly isAvailable?: boolean | null;
} | null> {
  if (row.destination_type === 'category' && row.destination_category_id) {
    const categoryQuery = (client.from as unknown as (relation: string) => QueryLike<unknown>)(
      'store_categories',
    );
    const { data: category } = await categoryQuery
      .select('id, name')
      .eq('id', row.destination_category_id)
      .eq('store_id', row.store_id)
      .is('deleted_at', null)
      .maybeSingle();
    const categoryRow = category as { readonly name?: string | null } | null;
    const storeSlug = await getStoreSlug(client, row.store_id);
    const productCounts = storeSlug
      ? await countPublicProductsByCategorySlug(client, storeSlug, [row.destination_category_id])
      : new Map<string, number>();
    return {
      categoryName: categoryRow?.name ?? null,
      isAvailable: (productCounts.get(row.destination_category_id) ?? 0) > 0,
    };
  }

  if (row.destination_type === 'product' && row.destination_product_id) {
    const productsQuery = (client.from as unknown as (relation: string) => QueryLike<unknown>)(
      'products',
    );
    const storeSlug = await getStoreSlug(client, row.store_id);
    const publicProductIds = storeSlug
      ? await getPublicProductIds(client, storeSlug, [row.destination_product_id])
      : new Set<string>();
    const { data: product } = await productsQuery
      .select('id, name, published, inventory, category:store_categories(id, name)')
      .eq('id', row.destination_product_id)
      .eq('store_id', row.store_id)
      .is('deleted_at', null)
      .maybeSingle();
    const productRow = product as {
      readonly name?: string | null;
      readonly published?: boolean | null;
      readonly inventory?: number | null;
      readonly category?: { readonly name?: string | null } | null;
    } | null;
    return {
      productName: productRow?.name ?? null,
      categoryName: productRow?.category?.name ?? null,
      isPublished: productRow?.published ?? null,
      available: productRow?.inventory ?? null,
      isAvailable: publicProductIds.has(row.destination_product_id),
    };
  }

  return null;
}

async function normalizeStoreAdMediaInvokeError(error: unknown): Promise<unknown> {
  const context = (error as { readonly context?: unknown } | null)?.context;
  if (typeof Response !== 'undefined' && context instanceof Response) {
    try {
      const value: unknown = await context.clone().json();
      if (typeof value === 'object' && value !== null) {
        const maybe = value as {
          readonly code?: unknown;
          readonly error?: unknown;
          readonly message?: unknown;
        };
        const code = typeof maybe.code === 'string' ? maybe.code : maybe.error;
        if (typeof code === 'string') {
          return {
            code,
            message: typeof maybe.message === 'string' ? maybe.message : code,
            statusCode: context.status,
          };
        }
      }
    } catch {
      // Keep the original Functions error if the body is not JSON.
    }
  }
  return error;
}

export async function listStorePromotionalAds(
  client: SupabaseClient,
  store: StorePromotionalAdStoreContext,
): Promise<StorePromotionalAdWithPreview[]> {
  const { data, error } = await promotionalAdsTable(client)
    .select('*')
    .eq('store_id', store.storeId)
    .is('deleted_at', null)
    .order('display_order', { ascending: true })
    .order('created_at', { ascending: true })
    .order('id', { ascending: true });

  if (error) throw error;
  const rows = (data ?? []) as StorePromotionalAd[];
  return Promise.all(rows.map((row) => hydrateAd(client, row)));
}

export async function getStorePromotionalAd(
  client: SupabaseClient,
  args: { readonly store_id: string; readonly store_slug: string; readonly ad_id: string },
): Promise<StorePromotionalAdWithPreview | null> {
  const { data, error } = await promotionalAdsTable(client)
    .select('*')
    .eq('id', args.ad_id)
    .eq('store_id', args.store_id)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return hydrateAd(client, data as StorePromotionalAd);
}

export async function createStorePromotionalAd(
  client: SupabaseClient,
  input: CreateStorePromotionalAdInput,
): Promise<StorePromotionalAdWithPreview> {
  const result = await invokeStoreAdMedia(client, 'create', input, input.image);
  return hydrateAd(client, result.ad, result.preview_url);
}

export async function updateStorePromotionalAd(
  client: SupabaseClient,
  input: UpdateStorePromotionalAdInput,
): Promise<StorePromotionalAdWithPreview> {
  if (input.image) {
    const result = await invokeStoreAdMedia(client, 'replace', input, input.image);
    return hydrateAd(client, result.ad, result.preview_url);
  }

  const patch: Record<string, unknown> = {};
  if (input.title !== undefined) patch.title = trimNullable(input.title);
  if (input.alt_text !== undefined) patch.alt_text = trimNullable(input.alt_text);
  if (input.destination !== undefined)
    Object.assign(patch, buildPromotionalAdDestinationColumns(input.destination));
  if (input.is_active !== undefined) patch.is_active = input.is_active;

  const { data, error } = await promotionalAdsTable(client)
    .update(patch)
    .eq('id', input.ad_id)
    .eq('store_id', input.store_id)
    .is('deleted_at', null)
    .select('*')
    .single();

  if (error) throw error;
  return hydrateAd(client, data as StorePromotionalAd);
}

export async function listPromotionalAdDestinationCategories(
  client: SupabaseClient,
  args: { readonly store_id: string; readonly search?: string | null; readonly limit?: number },
): Promise<PromotionalAdDestinationCategoryOption[]> {
  const cappedLimit = Math.min(Math.max(args.limit ?? 20, 1), 50);
  const storeSlug = await getStoreSlug(client, args.store_id);
  let query = (client.from as unknown as (relation: string) => QueryLike<unknown>)(
    'store_categories',
  )
    .select('id, name')
    .eq('store_id', args.store_id)
    .is('deleted_at', null)
    .order('display_order', { ascending: true })
    .order('created_at', { ascending: true })
    .limit(cappedLimit);

  const search = args.search?.trim();
  if (search) query = query.ilike('name', `%${search}%`);

  const { data, error } = await query;
  if (error) throw error;

  const categories = (data ?? []) as { readonly id: string; readonly name: string }[];
  if (categories.length === 0) return [];

  const productCounts = await countPublishedProductsByCategory(
    client,
    storeSlug,
    categories.map((category) => category.id),
  );

  return categories.map((category) => {
    const productCount = productCounts.get(category.id) ?? 0;
    return { id: category.id, name: category.name, productCount, isSelectable: productCount > 0 };
  });
}

export async function listPromotionalAdDestinationProducts(
  client: SupabaseClient,
  args: {
    readonly store_id: string;
    readonly search?: string | null;
    readonly category_id?: string | null;
    readonly limit?: number;
  },
): Promise<PromotionalAdDestinationProductOption[]> {
  const cappedLimit = Math.min(Math.max(args.limit ?? 20, 1), 50);
  const storeSlug = await getStoreSlug(client, args.store_id);
  let query = (client.from as unknown as (relation: string) => QueryLike<unknown>)('products')
    .select(
      'id, name, category_id, price_cents, sale_price_cents, currency, published, inventory, category:store_categories(id, name)',
    )
    .eq('store_id', args.store_id)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(cappedLimit);

  const search = args.search?.trim();
  if (search) query = query.ilike('name', `%${search}%`);
  if (args.category_id) query = query.eq('category_id', args.category_id);

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data ?? []) as {
    readonly id: string;
    readonly name: string;
    readonly category_id: string | null;
    readonly price_cents: number;
    readonly sale_price_cents: number | null;
    readonly currency: 'ILS';
    readonly published: boolean;
    readonly inventory: number;
    readonly category?: { readonly name?: string | null } | null;
  }[];
  const publicProductIds = await getPublicProductIds(
    client,
    storeSlug,
    rows.map((row) => row.id),
  );

  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    categoryId: row.category_id,
    categoryName: row.category?.name ?? null,
    price_cents: row.price_cents,
    sale_price_cents: row.sale_price_cents,
    currency: row.currency,
    published: row.published,
    available: row.inventory,
    isSelectable: publicProductIds.has(row.id),
  }));
}

async function countPublishedProductsByCategory(
  client: SupabaseClient,
  storeSlug: string | null,
  categoryIds: readonly string[],
): Promise<Map<string, number>> {
  if (!storeSlug || categoryIds.length === 0) return new Map();
  const { data, error } = (await (
    client.from as unknown as (relation: string) => QueryLike<unknown>
  )('products_public_published')
    .select('category_id')
    .eq('store_slug', storeSlug)
    .in('category_id', categoryIds)) as QueryResponse<unknown>;

  if (error) throw error;
  const counts = new Map<string, number>();
  for (const row of (data ?? []) as { readonly category_id: string | null }[]) {
    if (row.category_id) counts.set(row.category_id, (counts.get(row.category_id) ?? 0) + 1);
  }
  return counts;
}

async function countPublicProductsByCategorySlug(
  client: SupabaseClient,
  storeSlug: string,
  categoryIds: readonly string[],
): Promise<Map<string, number>> {
  return countPublishedProductsByCategory(client, storeSlug, categoryIds);
}

async function getPublicProductIds(
  client: SupabaseClient,
  storeSlug: string | null,
  productIds: readonly string[],
): Promise<Set<string>> {
  if (!storeSlug || productIds.length === 0) return new Set();
  const { data, error } = (await (
    client.from as unknown as (relation: string) => QueryLike<unknown>
  )('product_public_detail')
    .select('id')
    .eq('store_slug', storeSlug)
    .in('id', productIds)
    .limit(productIds.length)) as QueryResponse<unknown>;

  if (error) throw error;
  return new Set(((data ?? []) as { readonly id: string }[]).map((row) => row.id));
}

export async function reorderStorePromotionalAds(
  client: SupabaseClient,
  input: ReorderStorePromotionalAdsInput,
): Promise<void> {
  const { error } = await rpc(client)('reorder_store_promotional_ads', {
    target_store_id: input.store_id,
    ordered_ad_ids: input.ordered_ad_ids,
  });
  if (error) throw error;
}

export async function softDeleteStorePromotionalAd(
  client: SupabaseClient,
  input: DeleteStorePromotionalAdInput,
): Promise<void> {
  const { error } = await rpc(client)('soft_delete_store_promotional_ad', {
    target_ad_id: input.ad_id,
  });
  if (error) throw error;
}

export function mapPromotionalAdError(err: unknown): PromotionalAdErrorKey {
  if (!err || typeof err !== 'object') return 'common.errors.unknown';
  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'common.errors.networkFailed';
  }

  const maybe = err as SupabaseErrorLike;
  if (maybe.name === 'AuthRetryableFetchError') return 'common.errors.networkFailed';

  switch (maybe.code) {
    case 'imageRequired':
    case 'PROMOTIONAL_AD_imageRequired':
      return 'admin.promotionalAds.errors.mediaRequired';
    case 'imageInvalidType':
    case 'PROMOTIONAL_AD_imageInvalidType':
      return 'admin.promotionalAds.errors.mediaInvalidType';
    case 'imageTooLarge':
    case 'PROMOTIONAL_AD_imageTooLarge':
      return 'admin.promotionalAds.errors.mediaTooLarge';
    case 'imageDimensionsInvalid':
    case 'PROMOTIONAL_AD_imageDimensionsInvalid':
      return 'admin.promotionalAds.errors.mediaDimensionsInvalid';
    case 'PROMOTIONAL_AD_DESTINATION_INVALID':
    case '22023':
      return 'admin.promotionalAds.errors.externalHttpsRequired';
    case 'P0431':
      return 'admin.promotionalAds.errors.destinationTargetUnavailable';
    case 'P0421':
      return 'admin.promotionalAds.errors.tooManyAds';
    case 'P0422':
      return 'admin.promotionalAds.errors.tooManyActiveAds';
    case 'P0601':
      return 'admin.entitlements.promotionKitBlocked';
    case 'P0602':
      return 'admin.entitlements.storageLimitBlocked';
    case '42501':
    case 'forbidden':
    case 'unauthorized':
      return 'admin.promotionalAds.errors.permissionDenied';
    case 'P0002':
      return 'admin.promotionalAds.errors.notFound';
  }

  const fingerprint = [maybe.constraint ?? '', maybe.message ?? '', maybe.details ?? ''].join(' ');
  if (/title.*length/i.test(fingerprint)) return 'admin.promotionalAds.errors.titleTooLong';
  if (/alt.*length/i.test(fingerprint)) return 'admin.promotionalAds.errors.altTooLong';
  if (/storage quota exceeded/i.test(fingerprint)) {
    return 'admin.promotionalAds.errors.storageQuotaExceeded';
  }
  if (/billing storage limit/i.test(fingerprint)) {
    return 'admin.entitlements.storageLimitBlocked';
  }
  if (/billing entitlement required/i.test(fingerprint)) {
    return 'admin.entitlements.promotionKitBlocked';
  }
  if (/too many promotional ads/i.test(fingerprint))
    return 'admin.promotionalAds.errors.tooManyAds';
  if (/too many active promotional ads/i.test(fingerprint)) {
    return 'admin.promotionalAds.errors.tooManyActiveAds';
  }
  if (/mime|invalid type|not allowed|svg|html|video/i.test(fingerprint)) {
    return 'admin.promotionalAds.errors.mediaInvalidType';
  }
  if (/dimension|aspect/i.test(fingerprint)) {
    return 'admin.promotionalAds.errors.mediaDimensionsInvalid';
  }
  if (/reorder|permutation/i.test(fingerprint)) return 'admin.promotionalAds.errors.reorderInvalid';
  if (/permission denied|not authenticated/i.test(fingerprint)) {
    return 'admin.promotionalAds.errors.permissionDenied';
  }
  if (String(maybe.statusCode ?? '') === '413' || /payload too large/i.test(fingerprint)) {
    return 'admin.promotionalAds.errors.mediaTooLarge';
  }

  return 'common.errors.unknown';
}
